import * as React from 'react';
import { CSSProperties } from 'react';
import { colors } from '../features/employees/styles/personalakteStyles';

const shimmerKeyframes = `
@keyframes skeleton-shimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
`;

let styleInjected = false;
function injectStyles() {
  if (styleInjected) return;
  const style = document.createElement('style');
  style.textContent = shimmerKeyframes;
  document.head.appendChild(style);
  styleInjected = true;
}

const baseStyle: CSSProperties = {
  background: `linear-gradient(90deg, ${colors.bgMuted} 25%, ${colors.bgHover} 50%, ${colors.bgMuted} 75%)`,
  backgroundSize: '800px 100%',
  animation: 'skeleton-shimmer 1.8s ease-in-out infinite',
  borderRadius: 6,
};

interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  borderRadius?: number | string;
  style?: CSSProperties;
  circle?: boolean;
}

export function Skeleton({ width = '100%', height = 14, borderRadius, style, circle }: SkeletonProps) {
  React.useEffect(() => { injectStyles(); }, []);

  const resolvedRadius = circle ? '50%' : (borderRadius ?? 6);
  const resolvedWidth = circle && typeof height === 'number' ? height : width;

  return (
    <div
      style={{
        ...baseStyle,
        width: resolvedWidth,
        height,
        borderRadius: resolvedRadius,
        ...style,
      }}
    />
  );
}

interface ColumnDef {
  width: number | string;
  type?: 'text' | 'circle' | 'badge' | 'action';
}

interface TableSkeletonProps {
  columns: ColumnDef[];
  rows?: number;
  headerHeight?: number;
  rowHeight?: number;
}

export function TableSkeleton({ columns, rows = 8, headerHeight = 38, rowHeight = 44 }: TableSkeletonProps) {
  React.useEffect(() => { injectStyles(); }, []);

  const renderCell = (col: ColumnDef, i: number) => {
    const cellStyle: CSSProperties = {
      padding: '8px 10px',
      display: 'flex',
      alignItems: 'center',
      width: col.width,
      minWidth: col.width,
    };

    switch (col.type) {
      case 'circle':
        return (
          <td key={i} style={cellStyle}>
            <Skeleton circle height={32} />
          </td>
        );
      case 'badge':
        return (
          <td key={i} style={cellStyle}>
            <Skeleton width={60} height={22} borderRadius={9999} />
          </td>
        );
      case 'action':
        return (
          <td key={i} style={cellStyle}>
            <div style={{ display: 'flex', gap: 6 }}>
              <Skeleton width={28} height={28} borderRadius={8} />
              <Skeleton width={28} height={28} borderRadius={8} />
            </div>
          </td>
        );
      default:
        return (
          <td key={i} style={cellStyle}>
            <Skeleton width={typeof col.width === 'number' ? Math.min(col.width - 20, 120) : '80%'} height={14} />
          </td>
        );
    }
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ height: headerHeight, borderBottom: `1px solid ${colors.border}` }}>
          {columns.map((col, i) => (
            <th key={i} style={{ padding: '8px 10px', width: col.width, minWidth: col.width }}>
              <Skeleton width={typeof col.width === 'number' ? Math.min(col.width - 20, 80) : '60%'} height={12} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }, (_, rowIdx) => (
          <tr
            key={rowIdx}
            style={{
              height: rowHeight,
              borderBottom: `1px solid ${colors.borderLight}`,
              opacity: 1 - rowIdx * 0.08,
            }}
          >
            {columns.map(renderCell)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export const employeeColumns: ColumnDef[] = [
  { width: 36, type: 'circle' },
  { width: 160, type: 'text' },
  { width: 140, type: 'text' },
  { width: 120, type: 'text' },
  { width: 120, type: 'text' },
  { width: 70, type: 'text' },
  { width: 85, type: 'text' },
  { width: 85, type: 'text' },
  { width: 100, type: 'badge' },
  { width: 50, type: 'action' },
];

export const absenceColumns: ColumnDef[] = [
  { width: 35, type: 'action' },
  { width: 115, type: 'text' },
  { width: 80, type: 'badge' },
  { width: 100, type: 'text' },
  { width: 160, type: 'text' },
  { width: 45, type: 'text' },
  { width: 70, type: 'text' },
  { width: 75, type: 'badge' },
  { width: 80, type: 'text' },
  { width: 160, type: 'action' },
];

export const timeTrackingColumns: ColumnDef[] = [
  { width: 26, type: 'action' },
  { width: 115, type: 'text' },
  { width: 95, type: 'text' },
  { width: 80, type: 'text' },
  { width: 52, type: 'badge' },
  { width: 130, type: 'text' },
  { width: 50, type: 'text' },
  { width: 45, type: 'text' },
  { width: 55, type: 'text' },
  { width: 42, type: 'text' },
  { width: 140, type: 'action' },
];

export default Skeleton;
